/**
 * MapaVendedorDefesa.jsx
 * Mesmo layout do MapaVendedorPreview V1, mas somente lotes em DEFESA.
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { ChevronDown, Map, Printer, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const num = v => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0)
const fmtDate = s => { if (!s) return ''; try { return new Date(s).toLocaleDateString('pt-BR') } catch { return s } }

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, color: '#000', background: '#fff', padding: '16px 24px 20px', borderBottom: '2px solid #e5e7eb' },
  hdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #ccc', paddingBottom: 10, marginBottom: 14 },
  hdrLogo: { maxHeight: 64, maxWidth: 160, objectFit: 'contain' },
  hdrTitle: { flex: 1, padding: '0 20px', textAlign: 'center' },
  hdrT1: { fontSize: 16, fontWeight: 700 },
  hdrT2: { fontSize: 12, marginTop: 3 },
  compBlock: { marginBottom: 14 },
  compLbl: { fontSize: 11, fontWeight: 700, marginBottom: 3, fontStyle: 'italic' },
  compVal: { fontSize: 12, lineHeight: 1.3 },
  tbl: { width: '100%', borderCollapse: 'collapse', marginBottom: 10, fontSize: 12 },
  th: { background: '#d0d0d0', border: '1px solid #999', padding: '5px 6px', textAlign: 'center', fontWeight: 700, fontSize: 11 },
  td: { border: '1px solid #ccc', padding: '5px 6px', verticalAlign: 'top' },
  tdR: { border: '1px solid #ccc', padding: '5px 6px', textAlign: 'right', verticalAlign: 'top' },
  tdC: { border: '1px solid #ccc', padding: '5px 6px', textAlign: 'center', verticalAlign: 'top' },
  desc: { fontSize: 10, color: '#555', marginTop: 2 },
  totais: { textAlign: 'right', fontSize: 12, lineHeight: 1.8, marginBottom: 14 },
  footer: { textAlign: 'center', fontSize: 10, color: '#999', marginTop: 12, borderTop: '1px solid #eee', paddingTop: 6 },
}

function MapaPage({ vend, leilao, organizador }) {
  const lotes = vend.lotes_defesa || []

  let totalVv = 0, totalCv = 0, totalDesp = 0, qtdAnimais = 0

  lotes.forEach(l => {
    totalVv += l.vv || 0
    totalCv += l.cv_val || 0
    totalDesp += l.desp || 0
    qtdAnimais += l.qtd || 0
  })

  const logoSrc = organizador.logo_url
    ? (organizador.logo_url.startsWith('http') ? organizador.logo_url
        : `${API_BASE}${organizador.logo_url.startsWith('/') ? '' : '/'}${organizador.logo_url}`)
    : null

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.hdr}>
        <div>
          {logoSrc ? <img src={logoSrc} alt="logo" style={S.hdrLogo} />
            : <span style={{ fontWeight: 700, fontSize: 15 }}>{organizador.nome}</span>}
        </div>
        <div style={S.hdrTitle}>
          <div style={S.hdrT1}>MAPA DO VENDEDOR (DEFESAS)</div>
          <div style={S.hdrT2}>{organizador.nome}</div>
          <div style={S.hdrT2}>{leilao.nome} — {fmtDate(leilao.data_leilao)}</div>
        </div>
        <div style={{ minWidth: 80 }} />
      </div>

      {/* Dados vendedor */}
      <div style={S.compBlock}>
        <div style={S.compLbl}>Dados Vendedor</div>
        <div style={S.compVal}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{vend.nome.toUpperCase()} {vend.cpf_cnpj}</div>
          {vend.cidade && <div>{vend.cidade.toUpperCase()}{vend.uf ? ` - ${vend.uf.toUpperCase()}` : ''}</div>}
          {vend.telefone && <div>{vend.telefone}</div>}
        </div>
      </div>

      {/* Tabela lotes */}
      <table style={S.tbl}>
        <thead>
          <tr>
            <th style={{ ...S.th, width: '6%' }}>Lote</th>
            <th style={{ ...S.th, width: '30%' }}>Descrição</th>
            <th style={{ ...S.th, width: '8%' }}>Curral</th>
            <th style={{ ...S.th, width: '10%' }}>Situação</th>
            <th style={{ ...S.th, width: '6%' }}>Qtd</th>
            <th style={{ ...S.th, width: '13%' }}>Comissão</th>
            <th style={{ ...S.th, width: '13%' }}>Valor Unitário</th>
            <th style={{ ...S.th, width: '13%' }}>Valor Total</th>
          </tr>
        </thead>
        <tbody>
          {lotes.map((l, i) => {
            const desc = l.raca_resumo || [l.tipo_animal, l.raca, l.sexo, l.idade].filter(Boolean).join(' ').toUpperCase()
            return (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={S.tdC}><strong>{l.numero_lote}</strong></td>
                <td style={S.td}>{desc || '—'}</td>
                <td style={S.tdC}>{l.curral || '—'}</td>
                <td style={S.tdC}>{l.situacao || 'DEFESA'}</td>
                <td style={S.tdC}>{l.qtd}</td>
                <td style={S.tdR}>{brl(l.cv_val)}{l.comissao_valor_fixo && <div style={{ fontSize: 9, color: '#888' }}>(fixo)</div>}</td>
                <td style={S.tdR}>{brl(l.vv_unit)}</td>
                <td style={S.tdR}>{brl(l.vv)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* Totalizadores */}
      <div style={S.totais}>
        <div><strong>Vendas</strong> Lotes: 0  Qtde: 0</div>
        <div><strong>Defesas</strong> Lotes: {lotes.length}  Qtde: {qtdAnimais}</div>
        <div>Quantidade animais: {qtdAnimais}</div>
        <div style={{ marginTop: 8 }}>Total defesa: {brl(totalVv)}</div>
        <div>Comissão de defesa: {brl(totalCv)}</div>
        <div>Despesas: {brl(totalDesp)}</div>
        {(() => {
          // GTA+Nota em defesa (só se habilitado e fluxo gta_nota)
          const fluxoLeilao = leilao.fluxo_comissao || ''
          const cobrarDefesa = leilao.gta_cobrar_defesa || false
          let gtaNotaDefesa = 0
          if (fluxoLeilao === 'gta_nota' && cobrarDefesa) {
            const valorPorAnimal = parseFloat(leilao.valor_por_animal) || 0
            const valorNota = parseFloat(leilao.valor_nota) || 0
            const gtaModo = leilao.gta_modo_cobranca || 'por_vendedor'
            if (valorPorAnimal > 0 || valorNota > 0) {
              const gtaTotal = valorPorAnimal * qtdAnimais
              const notaTotal = gtaModo === 'por_lote' ? valorNota * lotes.length : valorNota * 1
              gtaNotaDefesa = gtaTotal + notaTotal
            }
          }
          return gtaNotaDefesa > 0 ? (
            <>
              <div>GTA + Nota: {brl(gtaNotaDefesa)}</div>
              <div style={{ fontWeight: 700 }}>Total de comissões: {brl(totalCv + totalDesp + gtaNotaDefesa)}</div>
            </>
          ) : (
            <div style={{ fontWeight: 700 }}>Total de comissões: {brl(totalCv + totalDesp)}</div>
          )
        })()}
      </div>

      <div style={S.footer}>www.rematei.com.br</div>
    </div>
  )
}

export default function MapaVendedorDefesa() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [vendedores, setVendedores] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [vendedorId, setVendedorId] = useState('')
  const [dados, setDados] = useState(null)
  const [loadingLeil, setLoadingLeil] = useState(false)
  const [loadingVend, setLoadingVend] = useState(false)
  const [loadingPrev, setLoadingPrev] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const [incluirNaoMov, setIncluirNaoMov] = useState(false)
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingLeil(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingLeil(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setVendedores([]); return }
    setLoadingVend(true)
    setDados(null)
    mapasService.getVendedoresDefesa(orgId, leilaoId, { incluirNaoMov })
      .then(list => { setVendedores(list); setVendedorId('') })
      .catch(() => setVendedores([]))
      .finally(() => setLoadingVend(false))
  }, [orgId, leilaoId, incluirNaoMov])

  const fetchDados = useCallback(async (vId) => {
    if (!orgId || !leilaoId) return
    setLoadingPrev(true)
    setDados(null)
    try {
      const result = await mapasService.getMapaVendedorDefesaDados(orgId, leilaoId, { vendedorId: vId || undefined, incluirNaoMov })
      setDados(result)
    } catch { setDados(null) }
    finally { setLoadingPrev(false) }
  }, [orgId, leilaoId, incluirNaoMov])

  useEffect(() => {
    if (!orgId || !leilaoId || loadingVend) return
    fetchDados(vendedorId)
  }, [vendedorId, leilaoId, orgId, loadingVend, incluirNaoMov]) // eslint-disable-line

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getMapaVendedorDefesaPdf(orgId, leilaoId, { vendedorId: vendedorId || undefined, incluirNaoMov })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) { alert('Erro: ' + (err.response?.statusText || err.message)) }
    finally { setLoadingPdf(false) }
  }
  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Map className="w-5 h-5 text-orange-500" />
        <h1 className="text-lg font-semibold text-gray-800">Mapa do Vendedor (Defesas{incluirNaoMov ? ' + Não Movimentados' : ''})</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Leilão" value={leilaoId} onChange={v => { setLeilaoId(v); setDados(null) }} disabled={loadingLeil} className="flex-[2] min-w-[180px]">
            {loadingLeil ? <option>Carregando...</option> : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
          </Select>
          <Select label="Vendedor" value={vendedorId} onChange={v => { setVendedorId(v); setDados(null) }} disabled={loadingVend || !leilaoId} className="flex-[2] min-w-[180px]">
            <option value="">TODOS OS VENDEDORES</option>
            {vendedores.map(v => <option key={v.id} value={String(v.id)}>{v.nome}</option>)}
          </Select>
          <div className="flex items-end gap-2 ml-auto">
            {loadingPrev && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir} disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir'}
            </button>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={incluirNaoMov} onChange={e => setIncluirNaoMov(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <span className="text-sm text-gray-700">Incluir Não Movimentados</span>
          </label>
        </div>
      </div>

      {dados && dados.vendedores && dados.vendedores.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div style={{ background: '#f3f4f6', padding: '24px 0' }}>
            {dados.vendedores.map((vend, i) => (
              <div key={vend.id || i} style={{ maxWidth: 900, margin: '0 auto 24px', background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.10)', borderRadius: 8, overflow: 'hidden' }}>
                <MapaPage vend={vend} leilao={dados.leilao} organizador={dados.organizador} />
              </div>
            ))}
          </div>
        </div>
      ) : !loadingPrev && leilaoId ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
          Nenhuma defesa encontrada para este leilão.
        </div>
      ) : null}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Mapa do Vendedor (Defesas)</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"><Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Mapa Vendedor Defesa" style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
